import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, AlertTriangle, Users, Car, DollarSign, XCircle, Scale, Mail } from 'lucide-react';

export default function Terms() {
  const navigate = useNavigate();
  const lastUpdated = "February 8, 2026";

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-dark-bg/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-neon-cyan" />
            <h1 className="text-xl font-bold">Terms & Conditions</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-dark-surface rounded-2xl p-6 md:p-8 border border-white/10">
          {/* Last Updated */}
          <p className="text-white/60 text-sm mb-6">Last Updated: {lastUpdated}</p>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-cyan mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Agreement to Terms
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Welcome to vgoride. These Terms and Conditions ("Terms") govern your access to and use of the vgoride 
              platform, including our website, mobile applications, and ride-hailing services (collectively, the "Service").
            </p>
            <p className="text-white/80 leading-relaxed mb-4">
              By creating an account, accessing, or using our Service, you agree to be bound by these Terms. If you 
              do not agree to these Terms, you may not access or use the Service.
            </p>
            <p className="text-white/80 leading-relaxed">
              vgoride operates in compliance with applicable federal laws of the United States, including regulations 
              enforced by the Federal Trade Commission (FTC), as well as the laws of the State of Kansas.
            </p>
          </section>

          {/* Eligibility */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-cyan mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Eligibility Requirements
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              To use our Service, you must:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
              <li>Be at least 18 years of age</li>
              <li>Have the legal capacity to enter into a binding agreement</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Not have been previously banned or removed from the Service</li>
            </ul>
            <p className="text-white/80 leading-relaxed mt-4">
              <strong>For Drivers:</strong> In addition to the above, you must possess a valid driver's license, 
              maintain required auto insurance, and meet all applicable state and local requirements for providing 
              transportation services in Kansas.
            </p>
          </section>

          {/* Service Description */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-cyan mb-4 flex items-center gap-2">
              <Car className="w-5 h-5" />
              Description of Service
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              vgoride provides a technology platform that connects riders seeking transportation with independent 
              drivers who provide transportation services. vgoride does not provide transportation services directly 
              and is not a transportation carrier.
            </p>
            <p className="text-white/80 leading-relaxed mb-4">
              <strong>Riders</strong> can request rides, view available drivers, schedule future rides, and 
              communicate with drivers through the platform.
            </p>
            <p className="text-white/80 leading-relaxed">
              <strong>Drivers</strong> are independent contractors, not employees of vgoride. They use their own 
              vehicles, set their own schedules, and are responsible for their own expenses, taxes, and compliance 
              with applicable laws.
            </p>
          </section>

          {/* Fares and Payment */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-cyan mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Fares and Payment
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Ride fares are calculated based on distance traveled, with the following pricing structure for rides 
              within the Wichita, Kansas area:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4 mb-4">
              <li>Minimum fare of $3 for rides under 3 miles</li>
              <li>$1 per mile (rounded up to the nearest dollar) for rides 3 miles or longer</li>
              <li>Additional $1 per passenger beyond the first passenger</li>
            </ul>
            <p className="text-white/80 leading-relaxed mb-4">
              Riders and drivers may negotiate and agree upon different fares. The final agreed-upon fare is 
              binding once a ride is confirmed.
            </p>
            <p className="text-white/80 leading-relaxed">
              Payment arrangements are made directly between riders and drivers. vgoride does not currently 
              process payments through the platform.
            </p>
          </section>

          {/* Cancellation Policy */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-cyan mb-4 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Cancellation Policy
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              <strong>For Riders:</strong> You may cancel a ride request at any time before a driver has arrived 
              at the pickup location. Repeated cancellations may result in temporary or permanent suspension of 
              your account.
            </p>
            <p className="text-white/80 leading-relaxed">
              <strong>For Drivers:</strong> You may reject or cancel a ride request before starting the trip. 
              Once a ride is in progress, cancellation should only occur for safety reasons or emergencies.
            </p>
          </section>

          {/* User Conduct */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-cyan mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              User Conduct and Safety
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              All users agree to:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4 mb-4">
              <li>Treat other users with respect and courtesy</li>
              <li>Not engage in any illegal, harmful, or discriminatory behavior</li>
              <li>Not use the Service for any unlawful purpose</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Not misrepresent your identity or provide false information</li>
              <li>Not attempt to defraud or deceive other users or vgoride</li>
            </ul>
            <p className="text-white/80 leading-relaxed">
              Violation of these conduct standards may result in immediate suspension or termination of your account.
            </p>
          </section>

          {/* Liability and Disclaimers */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-cyan mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Limitation of Liability
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, VGORIDE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
            </p>
            <p className="text-white/80 leading-relaxed mb-4">
              vgoride is a technology platform and does not assume any liability for the actions of drivers or 
              riders. Transportation services are provided by independent drivers, and vgoride makes no 
              representations or warranties regarding the quality, safety, or legality of such services.
            </p>
            <p className="text-white/80 leading-relaxed">
              <strong>Insurance:</strong> Drivers are solely responsible for maintaining adequate auto insurance 
              coverage as required by Kansas law. Riders are encouraged to verify that their driver has appropriate 
              coverage.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-cyan mb-4">
              Dispute Resolution
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Any disputes arising from these Terms or your use of the Service shall be governed by the laws of 
              the State of Kansas, without regard to conflict of law principles.
            </p>
            <p className="text-white/80 leading-relaxed">
              You agree to first attempt to resolve any dispute informally by contacting us at admin@vgoride.net. 
              If a dispute cannot be resolved informally, you agree that any legal action shall be brought 
              exclusively in the state or federal courts located in Sedgwick County, Kansas.
            </p>
          </section>

          {/* Modifications */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-cyan mb-4">
              Modifications to Terms
            </h2>
            <p className="text-white/80 leading-relaxed">
              vgoride reserves the right to modify these Terms at any time. We will notify users of material 
              changes by posting the updated Terms on our platform and updating the "Last Updated" date. Your 
              continued use of the Service after such changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-4">
            <h2 className="text-xl font-bold text-neon-cyan mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Contact Information
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="bg-dark-bg rounded-xl p-4 border border-white/10">
              <p className="text-white/80"><strong>vgoride</strong></p>
              <p className="text-white/80">Email: <a href="mailto:admin@vgoride.net" className="text-neon-cyan hover:underline">admin@vgoride.net</a></p>
              <p className="text-white/80">Support: <a href="mailto:customer-support@vgoride.net" className="text-neon-cyan hover:underline">customer-support@vgoride.net</a></p>
            </div>
          </section>

          {/* Footer Note */}
          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} vgoride. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
